import { CommonModule } from "@angular/common";
import { Component, inject, OnInit, AfterViewInit } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { MessageService } from "primeng/api";
import { filter, Observable, switchMap } from "rxjs";
import { DeleteCautionModalComponent } from "../../../components/modal-templates/delete-caution-modal/delete-caution-modal.component";
import { DeleteConfirmationComponent } from "../../../components/modal-templates/delete-confirmation/delete-confirmation.component";
import { EditorItemModalComponent } from "../../../components/modal-templates/generic-modal/editor-item-modal.component";
import { SharedImports } from "../../../components/shared.module";
import { AutomationActions } from "../../../core/ngrx/actions/automation.actions";
import {
  selectAutomationError,
  selectLoading,
  selectPages,
} from "../../../core/ngrx/selectors/automation.selectors";
import { pageDeleteTableColumns } from "../../../core/table-templates/application-delete-caution-table";
import { applicationDetail, mobileApplicationDetail } from "../../../core/table-templates/automation-application-table";
import { MessageEnum } from "../../../core/utils/message.enum";
import { BaseLayoutComponent } from "../../base-layout.component";

@Component({
  selector: "app-applications",
  standalone: true,
  imports: [...SharedImports, CommonModule],
  templateUrl: "./applications.component.html",
  styleUrl: "./applications.component.scss",
})
export class ApplicationsComponent
  extends BaseLayoutComponent
  implements OnInit, AfterViewInit {
  columns = applicationDetail;
  options = {
    isExpandable: false,
  };
  dataSource: any;
  pages$!: Observable<any>;
  loading$!: Observable<boolean>;
  error$!: Observable<string | null>;
  selectedPages: any = [];
  iconSrcDelete: string = '';
  private modalService = inject(NgbModal);

  ngAfterViewInit() {
    this.iconSrcDelete = `${this.basePath}assets/images/virtusa-icons/remove-icon-dark.svg`;
  }
  automationProjectID: string = "";
  projectID: string = "";
  applicationId: string = "";
  isLoading: boolean = false;
  addPageFields: any = [];
  private messageService = inject(MessageService);
  app: any;

  ngOnInit(): void {
    if (this.dataStore.activeNode?.paradigm?.toLowerCase() === 'web') {
      this.addPageFields = [
        {
          name: "Page Name",
          dataType: "string",
          id: "name",
          placeholder: "Page Name",
        },
        {
          name: "Page URL",
          dataType: "string",
          id: "url",
          placeholder: "Page URL",
        }
      ];
    } else {
      this.addPageFields = [
        {
          name: "Page Name",
          dataType: "string",
          id: "name",
          placeholder: "Page Name",
        }
      ];
      this.columns = mobileApplicationDetail;
    }
    this.pages$ = this.projectService.getApplicationId().pipe(
      filter((id: any) => !!id),
      switchMap((id: string) => this.store.select(selectPages(id)))
    );

    this.projectService.getAutomationProjectId().subscribe((id: any) => {
      this.automationProjectID = id;
    });

    this.projectService.getProjectId().subscribe((id) => {
      if (id != null) {
        this.projectID = id;
      }
    });
    this.projectService.getApplicationId().subscribe((id: any) => {
      this.applicationId = id;
    });

    this.automatiCScriptService.getActiveNode().subscribe((item: any) => {
      if (item.type === "application") {
        this.app = item;
      }
    });

    this.loading$ = this.store.select(selectLoading);
    this.error$ = this.store.select(selectAutomationError);

    this.loadNgrxData();
  }

  loadNgrxData() {
    this.pages$.subscribe((item) => {
      if (item) {
        const pagesList = Object.keys(item).map((key) => ({
          ...item[key],
          displayName: item[key]?.name,
          icon: "sprint",
          order: 1,
          level: 3,
          type: "pages",
          isAdd: true,
          view: "pageView",
          addApplicationTemplate: [
            {
              name: "UI Object Name",
              dataType: "string",
              id: "name",
              placeholder: "UI Object Name",
            },
          ],
        }));
        this.dataSource = pagesList;
      }
    });
  }

  addNewPage() {
    this.openGenericModal(false, {});
  }

  removePages() {
    this.isLoading = true;
    let idList = this.selectedPages.map((item: any) => item.id);

    this.restService
      .deleteValidationPage(
        this.projectID,
        this.automationProjectID,
        this.applicationId,
        idList
      )
      .subscribe({
        next: (response: any) => {
          this.isLoading = false;
          let rowData = response?.data;
          rowData = this.assignLevels(rowData);
          const deletableIds = this.selectedPages
            .filter((item: any) => !rowData?.some((d: any) => d.id === item.id))
            .map((item: any) => item.id);

          console.log("deletable ids ->", deletableIds);
          const data = {
            deleteList: deletableIds,
            skippedList: rowData,
          };

          if (
            Array.isArray(data?.skippedList) &&
            data?.skippedList?.length > 0
          ) {
            this.removePagesCaution(data);
          } else {
            this.removePagesConfirmation();
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          this.toaster.pop({ type: 'error', text: MessageEnum.AUTOMATION_PAGE_DELETE_VALIDATION_FAILURE });
        },
      });
  }

  assignLevels(data: any[], level: number = 0): any[] {
    return data.map(item => {
      const hasScript = this.containsScript(item);
      const newItem = {
        ...item,
        level,
        scriptLevel: hasScript
      };

      if (item.children && item.children.length > 0) {
        newItem.children = this.assignLevels(item.children, level + 1);
      }

      return newItem;
    });
  }

  containsScript(item: any): boolean {
    if (item.script) {
      return true;
    } else {
      return false;
    }
  }


  removePagesCaution(tableData: any) {
    this.isLoading = false;
    let nonDeletableAppCount = tableData.skippedList.length;
    let modalRef = this.modalService.open(DeleteCautionModalComponent, {
      centered: true,
      size: "lg",
      backdrop: "static",
    });
    modalRef.componentInstance.configs = {
      subTitle: "Unable to Delete All Selected Pages",
      cautionMessage1:
        nonDeletableAppCount +
        " of the selected pages are currently used in test scripts. To avoid breaking existing tests, these pages cannot be deleted.",
      cautionMessage2: "Only pages that are not currently in use will be removed.",
      footerMessage1:
        "You can delete the remaining pages that are not used in any scripts.",
      footerMessage2:
        "To delete the listed pages, first remove their references from the test scripts mentioned above.",
      tableData: tableData.skippedList,
      tableTitle: "Details of Pages in Use",
      deleteButtonText: "Delete Unused Pages Only",
      columns: pageDeleteTableColumns,
      projectId: this.projectID,
      automationProjectId: this.automationProjectID,
    };
    modalRef.result.then((result) => {
      if (result) {
        //delete pages / bulk
        this.store.dispatch(
          AutomationActions.deletePages({
            projectId: this.projectID,
            automationProjectId: this.automationProjectID,
            applicationId: this.applicationId,
            idList: tableData.deleteList
          })
        );
      }
    });
  }

  removePagesConfirmation() {
    this.isLoading = false;
    let modalRef = this.modalService.open(DeleteConfirmationComponent, {
      windowClass:"delete-confirmation-window",
      centered: true,
      size: "sm",
      backdrop: "static",
    });
    modalRef.componentInstance.data = {
      title: "Confirm Deletion",
      description: `Are you sure you want to delete Pages`,
    };
    modalRef.result.then((result) => {
      if (result) {
        //delete pages / bulk

        this.store.dispatch(
          AutomationActions.deletePages({
            projectId: this.projectID,
            automationProjectId: this.automationProjectID,
            applicationId: this.applicationId,
            idList: this.selectedPages.map((item: any) => item.id),
          })
        );
      }
    });
  }

  changeRemoveIcon(isActive: boolean) {
    this.iconSrcDelete = isActive
      ? `${this.basePath}assets/images/virtusa-icons/remove-icon-light.svg`
      : `${this.basePath}assets/images/virtusa-icons/remove-icon-dark.svg`;
  }

  handleRowSelect(event: any) {
    this.selectedPages = event?.data;
  }

  handleActionsInTable(event: any) {
    if (event.type == "svg") {
      this.openGenericModal(true, event.data?.row);
    }
  }

  openGenericModal(isEdit: boolean, row: any) {
    const modalParams = {
      title: isEdit ? "Update Page" : "Add New Page",
      isEdit: isEdit,
    };

    const modalRef = this.modalService.open(EditorItemModalComponent, {
      centered: true,
      windowClass: "add-item-modal",
      backdrop: 'static',
    });
    modalRef.componentInstance.configs = modalParams;
    if (isEdit) {
      const fields = this.addPageFields.map((field: any) => {
        return {
          ...field,
          value: row[field.id] || "",
        };
      });
      modalRef.componentInstance.fields = fields;
    } else {
      modalRef.componentInstance.fields = this.addPageFields;
    }
    modalRef.result.then((result: any) => {
      if (isEdit) {
        if (this.isNameExists(result?.value?.name, row?.id)) {
          this.toaster.pop({ type: 'error', text: MessageEnum.PAGE_NAME_DUPLICATE });
        } else {
          this.store.dispatch(
            AutomationActions.updatePage({
              projectId: this.projectID,
              automationProjectId: this.automationProjectID,
              applicationId: this.applicationId,
              id: row?.id,
              changes: {
                name: result?.value?.name,
                url: result?.value?.url,
              },
            })
          );
        }
      } else {
        if (this.isNameExists(result?.value?.name)) {
          this.toaster.pop({ type: 'error', text: MessageEnum.PAGE_NAME_DUPLICATE });
        } else {
          this.store.dispatch(
            AutomationActions.addPage({
              projectId: this.projectID,
              automationProjectId: this.automationProjectID,
              applicationId: this.applicationId,
              page: {
                name: result?.value?.name,
                url: result?.value?.url,
              },
            })
          );
        }
      }
    });
  }

  isNameExists(name: string, excludeId?: string): boolean {
    const normalizedName = name?.toLowerCase()?.trim();
    return this.dataSource
      ?.filter((page: any) => page?.id !== excludeId) // exclude current editing item
      ?.some((page: any) => page?.name?.toLowerCase()?.trim() === normalizedName);
  }

  refreshData() {
    this.store.dispatch(
      AutomationActions.loadPages({
        projectId: this.projectID,
        automationProjectId: this.automationProjectID,
        applicationId: this.applicationId,
      })
    );
  }
}
